import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, X, MousePointer2, Loader2 } from "lucide-react";
import useFullscreen from "@/hooks/useFullscreen";

const Viewer360 = ({ tour, tours = [], onClose, onSelectTour, soundOn = true, onToggleSound }) => {
  const isVideo = tour.media_type === "pano_video";

  const mountRef = useRef(null);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(null);

  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showHint, setShowHint] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    setLoading(true);
    setShowHint(true);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, 1100);
    camera.position.set(0, 0, 0.1);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);
    renderer.domElement.classList.add("viewer-canvas");

    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    let texture;
    let video;
    let disposed = false;

    if (isVideo) {
      video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.src = tour.video_url;
      videoRef.current = video;

      texture = new THREE.VideoTexture(video);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;

      const onReady = () => {
        setLoading(false);
        video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      };
      video.addEventListener("loadeddata", onReady);

      const onTime = () => {
        if (video.duration) setProgress((video.currentTime / video.duration) * 100);
      };
      video.addEventListener("timeupdate", onTime);
    } else {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin("anonymous");
      texture = loader.load(tour.video_url, () => {
        if (!disposed) setLoading(false);
      });
      texture.colorSpace = THREE.SRGBColorSpace;
    }

    const material = new THREE.MeshBasicMaterial({ map: texture });
    const sphere = new THREE.Mesh(geometry, material);
    scene.add(sphere);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = -0.3;
    controls.minDistance = 0.1;
    controls.maxDistance = 0.1;

    const onWheel = (e) => {
      e.preventDefault();
      camera.fov = THREE.MathUtils.clamp(camera.fov + e.deltaY * 0.03, 35, 90);
      camera.updateProjectionMatrix();
    };
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

    const onFirstInteract = () => setShowHint(false);
    renderer.domElement.addEventListener("pointerdown", onFirstInteract);

    const handleResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", handleResize);

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const hintTimer = setTimeout(() => setShowHint(false), 4000);

    return () => {
      disposed = true;
      clearTimeout(hintTimer);
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", handleResize);
      renderer.domElement.removeEventListener("wheel", onWheel);
      renderer.domElement.removeEventListener("pointerdown", onFirstInteract);
      if (video) {
        video.pause();
        video.src = "";
      }
      texture.dispose();
      geometry.dispose();
      material.dispose();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [tour.id, tour.video_url, isVideo]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 bg-black"
      data-testid="viewer-360"
      ref={containerRef}
    >
      <div ref={mountRef} className="absolute inset-0" />

      <AnimatePresence>
        {loading && (
          <motion.div
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#04271B] z-20 pointer-events-none"
            data-testid="viewer-loading"
          >
            <Loader2 className="w-8 h-8 text-[#D6B15E] animate-spin" strokeWidth={1.5} />
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-[#A9B8A6]">Loading immersive tour</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-6">
        <div className="backdrop-blur-2xl bg-black/40 border border-white/10 px-5 py-3 rounded-full">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D6B15E]">{tour.room}</p>
          <p className="font-heading text-lg leading-tight">{tour.title}</p>
        </div>
        <button
          onClick={onClose}
          data-testid="viewer-360-close-btn"
          className="w-12 h-12 flex items-center justify-center backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close viewer"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Drag hint */}
      <AnimatePresence>
        {showHint && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
          >
            <div className="flex flex-col items-center gap-3 backdrop-blur-md bg-black/30 border border-white/10 px-8 py-6 rounded-2xl">
              <MousePointer2 className="w-8 h-8 text-white animate-hint-sweep" strokeWidth={1.5} />
              <p className="text-sm tracking-[0.15em] uppercase text-white/90">Drag to look around</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-6 flex flex-col items-center gap-4">
        {tours.length > 1 && (
          <div className="flex gap-3 max-w-full overflow-x-auto pb-1">
            {tours.map((t) => (
              <button
                key={t.id}
                onClick={() => onSelectTour(t)}
                data-testid={`viewer-scene-${t.id}`}
                className={`shrink-0 px-4 py-2 rounded-full text-xs tracking-widest uppercase border transition-colors backdrop-blur-2xl ${
                  t.id === tour.id
                    ? "bg-[#D6B15E] text-black border-[#D6B15E]"
                    : "bg-black/40 text-white border-white/10 hover:bg-white/10"
                }`}
              >
                {t.room}
              </button>
            ))}
          </div>
        )}

        {isVideo ? (
          <div className="w-full max-w-2xl backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full px-4 py-3 flex items-center gap-4 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
            <button
              onClick={togglePlay}
              data-testid="viewer-play-toggle"
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-black hover:bg-gray-200 transition-colors shrink-0"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause className="w-5 h-5" strokeWidth={1.5} /> : <Play className="w-5 h-5 ml-0.5" strokeWidth={1.5} />}
            </button>
            <div className="flex-1 h-1 bg-white/15 rounded-full overflow-hidden">
              <div className="h-full bg-[#D6B15E] transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
            <button
              onClick={onToggleSound}
              data-testid="viewer-mute-toggle"
              title={soundOn ? "Mute ambient sound" : "Play ambient sound"}
              className="w-11 h-11 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/10 transition-colors shrink-0"
              aria-label={soundOn ? "Mute ambient sound" : "Play ambient sound"}
            >
              {soundOn ? <Volume2 className="w-5 h-5 text-[#D6B15E]" strokeWidth={1.5} /> : <VolumeX className="w-5 h-5" strokeWidth={1.5} />}
            </button>
            <button
              onClick={toggleFullscreen}
              data-testid="viewer-fullscreen-toggle"
              className="w-11 h-11 flex items-center justify-center rounded-full border border-white/10 hover:bg-white/10 transition-colors shrink-0"
              aria-label="Toggle fullscreen"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" strokeWidth={1.5} /> : <Maximize className="w-5 h-5" strokeWidth={1.5} />}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={onToggleSound}
              data-testid="viewer-mute-toggle"
              title={soundOn ? "Mute ambient sound" : "Play ambient sound"}
              className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label={soundOn ? "Mute ambient sound" : "Play ambient sound"}
            >
              {soundOn ? <Volume2 className="w-5 h-5 text-[#D6B15E]" strokeWidth={1.5} /> : <VolumeX className="w-5 h-5" strokeWidth={1.5} />}
            </button>
            <button
              onClick={toggleFullscreen}
              data-testid="viewer-fullscreen-toggle"
              className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full px-6 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors text-sm tracking-widest uppercase"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" strokeWidth={1.5} /> : <Maximize className="w-4 h-4" strokeWidth={1.5} />}
              Fullscreen
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Viewer360;
